import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
	Modal,
	ModalBody,
	Button,
	Form,
	FormGroup,
	Row,
	Col,
	Input,
	FormFeedback,
	Label,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import organizationalStructureService from "services/OrganizationalStructureService";
import ataDefinitionsTagsService from "services/AtaDefinitionTagService";
import ataDefinitionService from "services/AtaDefinicoesService";
import ModalTags from "./ModalTags";
import { FloppyDisk, Plus, SignOut } from "@phosphor-icons/react";

const ModalCreateEditDefinition = ({ show, onCloseClick, data }) => {
	const id = data?.id;
	const [definitionsTags, setDefinitionTags] = useState([]);
	const [departamentos, setDepartamentos] = useState([]);
	const [selectedOptions, setSelectedOptions] = useState([]);
	const [showModalTags, setShowModalTags] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		if (!show) {
			validation.resetForm();
			setSelectedOptions([]);
		}
	}, [show]);

	const validation = useFormik({
		enableReinitialize: true,
		initialValues: {
			definition: data?.definition || "",
			ata_id: data?.ata_id || data || "",
			organizational_structure_id:
				data?.organizational_structure_id || "",
			included_at:
				data?.included_at ||
				(!id ? new Date().toISOString().split("T")[0] : ""),
			ata_definitions_tags: [],
		},
		validationSchema: Yup.object({
			definition: Yup.string().required(
				"Por favor, informe a definição!",
			),
			organizational_structure_id: Yup.string().required(
				"Por favor, informe departamento!",
			),
			included_at: Yup.string().required("Por favor, informe a data!"),
		}),
		onSubmit: async (values) => {
			let val = { ...values };

			if (selectedOptions.length > 0) {
				val.ata_definitions_tags = selectedOptions.map(
					(sl) => sl.value,
				);
			}

			setIsLoading(true);
			try {
				if (id) {
					await ataDefinitionService.put(id, val);
				} else {
					await ataDefinitionService.post(val);
				}
				onCloseClick();
			} catch (error) {
				console.error("Erro ao salvar a definição:", error);
			} finally {
				setIsLoading(false);
			}
		},
	});

	const customStyles = {
		option: (provided, state) => ({
			...provided,
			color: state.isSelected ? "blue" : "black",
			backgroundColor: "white",
			"&:hover": {
				backgroundColor: "#556ee6",
			},
		}),
	};

	const getDepartamentos = async () => {
		try {
			const response = await organizationalStructureService.get();

			const options = response.map((item) => ({
				value: item.id,
				label: item.name,
			}));

			setDepartamentos(options);
		} catch (error) {
			console.error("Erro ao buscar departamentos:", error);
		}
	};

	const getDefinitionTags = async () => {
		if (!id) return;
		try {
			const response = await ataDefinitionsTagsService.get({
				ata_definitions_id: id,
			});
			const tags = response.map((item) => ({
				value: item.id,
				label: item.tag,
			}));
			setDefinitionTags(tags);
			setSelectedOptions(tags);
		} catch (error) {
			console.error("Erro ao buscar tags de definição:", error);
		}
	};

	const showModalNewTag = () => {
		setShowModalTags(true);
	};

	const closeModalNewTag = () => {
		setShowModalTags(false);
	};

	const handleChange = (selectedOption) => {
		setSelectedOptions(selectedOption);
	};

	useEffect(() => {
		if (show) {
			getDepartamentos();
			getDefinitionTags();
		}
	}, [show, showModalTags]);

	return (
		<Modal
			size="lg"
			isOpen={show}
			toggle={onCloseClick}
			centered={true}
			style={{ minWidth: 800 }}
		>
			<div className="modal-content">
				<ModalBody className="px-4 py-3">
					<Form
						className="needs-validation"
						onSubmit={(e) => {
							e.preventDefault();
							validation.handleSubmit();
							return false;
						}}
					>
						<FormGroup className="mb-3">
							<Label htmlFor="definition">
								{id ? "Editar Definição" : "Nova Definição"}{" "}
								<span style={{ color: "red" }}>*</span>
							</Label>
							<Input
								name="definition"
								type="textarea"
								placeholder="Insira uma definição."
								onChange={(value) => {
									validation.handleChange(value);
								}}
								onBlur={validation.handleBlur}
								value={validation.values.definition}
								invalid={
									validation.touched.definition &&
									!!validation.errors.definition
								}
							/>
							{validation.touched.definition &&
								validation.errors.definition && (
									<FormFeedback type="invalid">
										{validation.errors.definition}
									</FormFeedback>
								)}
						</FormGroup>
						<Row>
							{id && (
								<Col md="4">
									<FormGroup className="mb-3">
										<Label htmlFor="tags">Tags</Label>
										<div className="d-flex align-items-center mb-2">
											<Select
												isMulti
												styles={customStyles}
												placeholder="Tags"
												onChange={(selectedOption) => {
													handleChange(
														selectedOption,
													);
													const selectedValue =
														selectedOption?.map(
															(selected) =>
																selected.value,
														);
													validation.setFieldValue(
														"ata_definitions_tags",
														selectedValue,
													);
												}}
												options={definitionsTags}
												value={selectedOptions}
												className="flex-grow-1 me-2"
											/>
											<Button
												size="sm"
												onClick={showModalNewTag}
												className="d-flex align-items-center justify-content-center"
												style={{
													background: "none",
													cursor: "pointer",
													color: "black",
													minWidth: "36px",
													minHeight: "36px",
												}}
											>
												<Plus size={16} />
											</Button>
										</div>
									</FormGroup>
								</Col>
							)}

							<Col md={id ? 4 : 6}>
								<FormGroup className="mb-3">
									<Label htmlFor="organizational_structure_id">
										Departamento{" "}
										<span style={{ color: "red" }}>*</span>
									</Label>
									<Select
										styles={customStyles}
										placeholder="Selecione"
										onChange={(selectedOption) => {
											const selectedValue = selectedOption
												? selectedOption.value
												: "";
											validation.setFieldValue(
												"organizational_structure_id",
												selectedValue,
											);
										}}
										value={
											departamentos.find(
												(option) =>
													option.value ===
													validation.values
														.organizational_structure_id,
											) || null
										}
										options={departamentos}
										onBlur={validation.handleBlur}
										className={
											validation.touched
												.organizational_structure_id &&
											validation.errors
												.organizational_structure_id
												? "is-invalid"
												: ""
										}
									/>
									{validation.touched
										.organizational_structure_id &&
										validation.errors
											.organizational_structure_id && (
											<FormFeedback
												type="invalid"
												className="d-block"
											>
												{
													validation.errors
														.organizational_structure_id
												}
											</FormFeedback>
										)}
								</FormGroup>
							</Col>
							<Col md={id ? 4 : 6}>
								<FormGroup className="mb-3">
									<Label htmlFor="included_at">
										Data de inclusão{" "}
										<span style={{ color: "red" }}>*</span>
									</Label>
									<Input
										name="included_at"
										type="date"
										placeholder="Data de inclusão"
										onChange={validation.handleChange}
										onBlur={validation.handleBlur}
										value={validation.values.included_at}
										invalid={
											validation.touched.included_at &&
											!!validation.errors.included_at
										}
									/>
									{validation.touched.included_at &&
										validation.errors.included_at && (
											<FormFeedback type="invalid">
												{validation.errors.included_at}
											</FormFeedback>
										)}
								</FormGroup>
							</Col>
						</Row>
						<Row className="py-1">
							<div className="hstack gap-2 justify-content-center">
								<Button
									style={{
										background: "none",
										padding: 10,
										cursor: "pointer",
										color: "black",
										marginLeft: 10,
										width: 150,
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										gap: 2,
									}}
									type="submit"
									disabled={isLoading}
								>
									{isLoading ? (
										<span
											className="spinner-border spinner-border-sm mr-1"
											style={{ marginRight: 10 }}
										></span>
									) : null}
									<FloppyDisk size={16} /> Salvar
								</Button>
								<Button
									type="button"
									style={{
										background: "none",
										padding: 10,
										cursor: "pointer",
										color: "black",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										gap: 2,
									}}
									onClick={onCloseClick}
								>
									<SignOut size={16} /> Fechar
								</Button>
							</div>
						</Row>
					</Form>
				</ModalBody>
			</div>
			{id && (
				<ModalTags
					show={showModalTags}
					onCloseClick={closeModalNewTag}
					data={id}
				/>
			)}
		</Modal>
	);
};

ModalCreateEditDefinition.propTypes = {
	show: PropTypes.bool.isRequired,
	onCloseClick: PropTypes.func.isRequired,
	data: PropTypes.object,
};

export default ModalCreateEditDefinition;
